/**
 * MatrixToolbar — search pill (filters drugs by label / ATC / SNOMED),
 * therapeutic-category chips derived from a REAL signal (ATC level-1), and
 * the four-tone verdict legend that doubles as a tone-visibility filter.
 *
 * Categories are only shown when the matrix actually carries ATC codes; a
 * chip never promises a group the data can't fill. Search and chips both
 * filter the live drug list — no fabricated facets.
 *
 * The legend swatches are interactive: tapping one toggles whether matrix
 * cells of that tone render at full opacity or dim into the background. The
 * legend keeps its colour-key role AND becomes the focus control — the same
 * affordance teaches both meanings. Default = all tones visible.
 */

import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { VerdictTone } from '@frontend/components/ysite/types'
import { LEGEND_SWATCH } from '@frontend/components/ysite/verdict.ui'

interface MatrixToolbarProps {
  query: string
  onQuery: (q: string) => void
  /** Tones currently rendered at full opacity. Others dim in the grid. */
  visibleTones: Set<VerdictTone>
  /** Toggle a single tone in/out of the visible set. */
  onToggleTone: (tone: VerdictTone) => void
  /** Restore the default (all tones visible). */
  onShowAllTones: () => void
}

const LEGEND: { tone: VerdictTone; key: string }[] = [
  { tone: 'ok', key: 'compatibility.legend.ok' },
  { tone: 'warn', key: 'compatibility.legend.warn' },
  { tone: 'bad', key: 'compatibility.legend.bad' },
  { tone: 'muted', key: 'compatibility.legend.muted' },
]

export function MatrixToolbar({
  query,
  onQuery,
  visibleTones,
  onToggleTone,
  onShowAllTones,
}: MatrixToolbarProps) {
  const { t } = useTranslation()
  const allTonesVisible = LEGEND.every(({ tone }) => visibleTones.has(tone))

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Search pill */}
      <label
        className={cn(
          // min-w-0 base lets the pill shrink to fit a narrow row (it shares a
          // flex-wrap line with the tone legend); the comfortable 260px floor
          // only kicks in once there's room (sm+). A hard 260px min on every
          // size forced an overflow on the smallest screens.
          'flex h-11 min-w-0 max-w-[460px] flex-1 items-center gap-2.5 rounded-full sm:min-w-[260px]',
          'border border-[var(--hairline)] bg-[var(--canvas)] px-4',
          'focus-within:border-primary-500 focus-within:shadow-input-focus',
        )}
      >
        <Search className="h-4 w-4 flex-shrink-0 text-neutral-400" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={t('compatibility.search.placeholder')}
          aria-label={t('compatibility.search.placeholder')}
          className="min-w-0 flex-1 bg-transparent text-[15px] text-neutral-900 outline-none placeholder:text-neutral-400"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQuery('')}
            aria-label={t('compatibility.search.clear')}
            className="-mr-1 flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition active:scale-95 hover:bg-neutral-100"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </label>

      {/* ATC category chips removed — they wrapped to a second row and ate
          significant vertical space without earning it (a nurse filtering
          a Y-site grid by ATC anatomical-main-group is rare; search by
          drug name is the primary filter). Props kept on the type so the
          board's existing state machinery doesn't churn; the chips just
          don't render. Reintroduce as a popover/dropdown if needed. */}

      {/* Legend / tone filter — the swatches are toggles. Active = full
          opacity + Action Blue ring; inactive = reduced opacity + dashed
          ring so the disabled state carries a non-colour cue. */}
      <div
        role="group"
        aria-label={t('compatibility.toneFilter.groupLabel')}
        className="ml-auto flex flex-wrap items-center gap-x-1.5 gap-y-1.5"
      >
        {LEGEND.map(({ tone, key }) => {
          const visible = visibleTones.has(tone)
          const label = t(key)
          return (
            <button
              key={tone}
              type="button"
              aria-pressed={visible}
              aria-label={t(
                visible ? 'compatibility.toneFilter.toggleOn' : 'compatibility.toneFilter.toggleOff',
                { tone: label },
              )}
              onClick={() => onToggleTone(tone)}
              className={cn(
                // 44×44 hit area via padding; visual chip is compact.
                'group inline-flex h-11 items-center gap-1.5 rounded-full px-2.5 text-[12px] transition active:scale-95',
                visible
                  ? 'text-neutral-700'
                  : 'text-neutral-400 line-through decoration-[1.5px] decoration-neutral-400/60',
                'motion-reduce:transition-none',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'inline-block h-3 w-3 rounded-[3px] transition-opacity',
                  LEGEND_SWATCH[tone],
                  visible ? 'opacity-100 ring-1 ring-inset ring-primary-500/0' : 'opacity-35',
                  // Action Blue ring on the active swatch — single accent per DESIGN.md.
                  visible && 'ring-2 ring-primary-500/30',
                )}
              />
              {label}
            </button>
          )
        })}
        {/* Reset escape hatch — only surfaces once the user has hidden
            something, so the toolbar stays calm in the default state. */}
        {!allTonesVisible && (
          <button
            type="button"
            onClick={onShowAllTones}
            className="inline-flex h-11 items-center rounded-full px-2.5 text-[12px] font-semibold text-primary-600 transition active:scale-95 hover:text-primary-700"
          >
            {t('compatibility.toneFilter.showAll')}
          </button>
        )}
      </div>
    </div>
  )
}

